/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import * as React from 'react';
import { useContext } from 'react';
import { UIDReset } from 'react-uid';
import { LayersManager } from '../layer';
import { ThemeProvider } from '../styles';
import { Base2FeaturesContext } from './base2-features-context';
import { Base2FeaturesProvider } from './base2-features-provider';
import type { BaseProviderProps } from './types';

/**
 * Provides theme and layers management for BaseUI components.
 *
 * Token-delivery logic lives in `ThemeProvider`; BaseProvider forwards the
 * relevant props to it and adds `LayersManager` and `UIDReset`. `ThemeProvider`
 * wraps `LayersManager` so the CSS-driven scope covers both the app content and
 * the overlay portal host (modals, popovers, tooltips).
 *
 * The whole subtree is re-published through `Base2FeaturesProvider`, seeded with
 * whatever `Base2FeaturesContext` already holds — the fusion plugin's value when
 * it is registered, `undefined` when it is not. That provider is what folds the
 * document-root `data-base-version-*` overrides into the context, so the
 * attributes reach every consumer below (the theme upgrades in `ThemeProvider`,
 * `Block`'s Typography 2.0 letter-spacing) in apps that never register the
 * plugin. Re-publishing an already-merged value is a no-op.
 */
const BaseProvider = (props: BaseProviderProps) => {
  const {
    children,
    cssTheming,
    overrides,
    product,
    theme,
    themeMode,
    zIndex
  } = props;
  const inheritedBase2Features = useContext(Base2FeaturesContext);
  return <Base2FeaturesProvider value={inheritedBase2Features}>
      <ThemeProvider theme={theme} cssTheming={cssTheming} product={product} themeMode={themeMode}>
        <LayersManager zIndex={zIndex} overrides={overrides}>
          <UIDReset prefix="bui">{children}</UIDReset>
        </LayersManager>
      </ThemeProvider>
    </Base2FeaturesProvider>;
};
export default BaseProvider;